<template>
  <div class="task-list">
    <!-- 任务列表（企业微信手机端，仅查看；看板在手机上改为列表展示） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="任务编号/标题" @keyup.enter="queryList"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="queryList">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.projectId" :data="projectOptions"
                                 label-column="projectName" code-column="id" type="list"
                                 placeholder="所属项目" @change="onProjectChange"/>
        <mobile-condition-select v-model="searchInfo.taskStatus" :data="taskStatusOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="任务状态" @change="queryList"/>
        <mobile-search-select v-model="searchInfo.ownerId" :query="queryEmployeePage" size="small"
                              label-column="employeeName" code-column="id"
                              :display-text="searchInfo.ownerName" placeholder="负责人"
                              search-placeholder="职员名称/手机号" @change="onOwnerChange"/>
      </div>
    </mobile-list-header>

    <div class="task-body" v-loading="loading">
      <list-card class="task-item" v-for="item in dataList" :key="item.id" :label="item.taskName">
        <template #desc>
          <span v-if="isDelayed(item)" class="delay-tag">{{ delayText(item) }}</span>
          <span class="status-tag" :class="'tag-' + taskStatusTag(item.taskStatus)">
            {{ taskStatusLabel(item.taskStatus) || "—" }}
          </span>
        </template>
        <data-column label="任务编号">{{ item.taskCode || "—" }}</data-column>
        <data-column label="所属项目">
          {{ item.projectName || item.projectCode || "—" }}{{ item.sprintName ? " / " + item.sprintName : "" }}
        </data-column>
        <data-column label="负责人">{{ item.ownerName || "—" }}</data-column>
        <data-column label="计划时间">
          {{ dateOnly(item.planStart) }} ~ {{ dateOnly(item.planEnd) }}
        </data-column>
        <data-column label="预估工时">{{ item.estimate === null || item.estimate === undefined ? "—" : item.estimate + "人天" }}</data-column>
        <div class="progress-row">
          <div class="progress-track">
            <i :style="{width: Math.max(0, Math.min(100, Number(item.progress) || 0)) + '%'}"/>
          </div>
          <span class="progress-text">{{ Number(item.progress) || 0 }}%</span>
        </div>
      </list-card>
      <div v-if="!loading && dataList.length === 0" class="empty">暂无任务</div>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import taskApi from "@/api/dz-project/project/taskApi";
import commonApi from "@/api/commonApi";
import {TASK_STATUS_OPTIONS, taskStatusLabel, taskStatusTag} from "@/views/h5/crm/project/manage/projectStatus";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "TaskListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileSearchSelect, MobileConditionSelect, MobileListHeader
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        projectId: "",
        taskStatus: "",
        ownerId: "",
        ownerName: "",
      },
      dataList: [],
      projectOptions: [],
      taskStatusOptions: TASK_STATUS_OPTIONS,
    };
  },
  created() {
    // 允许从项目页/消息链接带项目过滤进入
    const query = this.route.query || {};
    if (query.projectId) {
      this.searchInfo.projectId = query.projectId;
    }
    taskApi.queryProjectOptions().then(list => this.projectOptions = list || []);
    this.queryList();
  },
  methods: {
    taskStatusLabel,
    taskStatusTag,
    /** 看板接口返回的是全量列表，不分页 */
    queryList() {
      this.loading = true;
      taskApi.queryBoard(this.cleanParams()).then(list => {
        this.dataList = list || [];
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      })
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.queryList();
    },
    cleanParams() {
      const params = {};
      ["searchKey", "projectId", "taskStatus", "ownerId"].forEach(key => {
        const value = this.searchInfo[key];
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      return params;
    },
    onProjectChange() {
      this.queryList();
    },
    onOwnerChange(record) {
      this.searchInfo.ownerName = record ? (record.employeeName || "") : "";
      this.queryList();
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    isDelayed(item) {
      return item.delayState && item.delayState !== "正常";
    },
    delayText(item) {
      if (item.delayState === "延期中") {
        return "延期" + (item.delayDays || "") + "天";
      }
      return item.delayState || "";
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
  }
};
</script>

<style lang="scss" scoped>
.task-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.task-body{
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
}
.task-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .delay-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .delay-tag{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .tag-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .tag-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-success{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .tag-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .progress-row{
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    .progress-track{
      flex: 1;
      height: 6px;
      border-radius: 3px;
      background: var(--yq-background-color);
      overflow: hidden;
      i{
        display: block;
        height: 100%;
        border-radius: 3px;
        background: var(--el-color-primary);
        transition: width .3s;
      }
    }
    .progress-text{
      flex: none;
      min-width: 40px;
      text-align: right;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
      font-variant-numeric: tabular-nums;
    }
  }
}
.empty{
  padding: 30px 0;
  text-align: center;
  font-size: var(--yq-font-size-mini-mobile);
  color: var(--yq-font-color-desc);
}
</style>
